import type { DoneItem } from '../types';
import { FilterBar, LoadMore, ListEmpty, MoreInOtherMonths, applySearch, sortBy, takeLoaded } from '@/components/lists';
import { last30Cutoff } from '../historyMock';

const STEP = 20;

function textOf(d: DoneItem): string {
  return `${d.kindLabel} ${d.title} ${d.outcome} ${d.by} ${d.dateLabel ?? ''}`;
}

/**
 * The "Done" history (00-ds-09 §2, D148): last-30-days window by default, a
 * search, and "Show 20 more" — a load-more queue, not numbered pages. All
 * client-side over the mock history; the server owns this in production (§8).
 */
export function DoneHistory({
  rows,
  q,
  win,
  loaded,
  onSearch,
  onWindow,
  onMore,
  onBack,
}: {
  rows: DoneItem[];
  q: string;
  /** '30' = last 30 days (default), 'all' = all time. */
  win: '30' | 'all';
  loaded: number;
  onSearch: (v: string) => void;
  onWindow: (v: '30' | 'all') => void;
  onMore: () => void;
  onBack: () => void;
}) {
  const cutoff = last30Cutoff();
  const windowed = win === 'all' ? rows : rows.filter((x) => (x.dateIso ?? '9999') >= cutoff);
  const searched = sortBy(applySearch(windowed, q, textOf), (d) => d.dateIso ?? '', -1);
  const visible = takeLoaded(searched, loaded);

  // When a search is on and we're inside the 30-day window, how many more
  // matches live in earlier months (so we can offer "Search all time").
  const allMatches = q.trim() !== '' ? applySearch(rows, q, textOf).length : 0;
  const outsideWindow = win === '30' ? Math.max(0, allMatches - searched.length) : 0;

  return (
    <div className="hl-history">
      <div className="hl-history-head">
        <button type="button" className="hl-src" onClick={onBack}>
          ← Back to what needs a person
        </button>
        <h2 className="hl-history-title">
          Done {win === 'all' ? 'all time' : 'in the last 30 days'} · {searched.length.toLocaleString()}
        </h2>
      </div>

      <FilterBar
        search={q}
        onSearch={onSearch}
        searchPlaceholder="Search done items — patient, payer, who closed it"
        showDateRange={false}
        right={
          <button type="button" className="hl-src" onClick={() => { onWindow(win === '30' ? 'all' : '30'); }}>
            {win === '30' ? 'Show all time' : 'Show last 30 days'}
          </button>
        }
      />

      {searched.length === 0 ? (
        <ListEmpty
          {...(q.trim() !== '' ? { query: q } : {})}
          {...(win === '30' ? { windowLabel: 'the last 30 days' } : {})}
          {...(win === '30' && outsideWindow > 0 ? { onSearchAllTime: () => { onWindow('all'); } } : {})}
        />
      ) : (
        <>
          <div className="hl-history-list">
            {visible.map((d) => (
              <div key={d.id} className="hl-history-row">
                <div className="hl-history-rtop">
                  <span className="hl-kind">{d.kindLabel}</span>
                  <span className="hl-rmeta">{d.dateLabel ?? ''}</span>
                </div>
                <div className="hl-rtitle">{d.title}</div>
                <div className="hl-rmeta">{d.outcome} · {d.by}</div>
              </div>
            ))}
          </div>
          <LoadMore loaded={visible.length} total={searched.length} step={STEP} onMore={onMore} />
          {win === '30' ? <MoreInOtherMonths count={outsideWindow} onShowAllTime={() => { onWindow('all'); }} /> : null}
        </>
      )}
    </div>
  );
}
